import { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

const NAV_ITEMS = [
  {
    label: "Pre-Letter Before Action",
    desc: "Send formal pre-LBA notices to debtors",
    route: "/lba",
    iconBg: "#EFF6FF",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <path d="M9 22h6c5 0 7-2 7-7V9c0-5-2-7-7-7H9C4 2 2 4 2 9v6c0 5 2 7 7 7Z"
          stroke="#2563EB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M15.75 9h-7.5M15.75 15h-7.5"
          stroke="#2563EB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
  {
    label: "Post Pre-LBA Actions",
    desc: "Follow-up steps after issuing a pre-LBA",
    route: "/postlba",
    iconBg: "#F5F3FF",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <path d="M15.24 2H8.76C5 2 4.71 5.38 6.74 7.22l10.52 9.56C19.29 18.62 19 22 15.24 22H8.76C5 22 4.71 18.62 6.74 16.78l10.52-9.56C19.29 5.38 19 2 15.24 2Z"
          stroke="#7C3AED" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
  {
    label: "Late Payment Demand",
    desc: "Issue overdue payment demands quickly",
    route: "/lpd",
    iconBg: "#FFFBEB",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <path d="M12 6V2M12 22v-4M6 12H2M22 12h-4M7.76 7.76 4.93 4.93M19.07 19.07l-2.83-2.83M7.76 16.24l-2.83 2.83M19.07 4.93l-2.83 2.83"
          stroke="#D97706" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z"
          stroke="#D97706" strokeWidth="1.5"/>
      </svg>
    ),
  },
  {
    label: "Defence & Case Handling",
    desc: "Manage disputes and legal case responses",
    route: "/defence1",
    iconBg: "#F0FDF4",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"
          stroke="#059669" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="m3.27 6.96 8.73 5.05 8.73-5.05M12 22.08V12"
          stroke="#059669" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
];

const NavigationDropdown = ({ isMobile, onClose }) => {
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) onClose?.();
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  const handleNav = useCallback((route) => {
    navigate(route);
    onClose?.();
  }, [navigate, onClose]);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;600;700&display=swap');
        .nd-root { font-family: 'DM Sans', sans-serif; }
        .nd-item {
          display: flex; align-items: flex-start; gap: 12px;
          padding: 14px 16px; border-radius: 12px;
          border: 1px solid #E2E8F0; background: #F8FAFC;
          cursor: pointer; transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
          text-align: left; width: 100%;
        }
        .nd-item:hover {
          border-color: #93C5FD; background: #EFF6FF;
          box-shadow: 0 2px 8px rgba(37,99,235,0.08);
        }
        .nd-item:hover .nd-title { color: #1D4ED8; }
      `}</style>

      <div
        ref={dropdownRef}
        className="nd-root"
        style={{
          width: "100vw",
          background: "#ffffff",
          borderBottom: "1px solid #E2E8F0",
          boxShadow: !isMobile ? "0 4px 16px rgba(0,0,0,0.06)" : "none",
          padding: isMobile ? "20px 16px 24px" : "20px 96px 28px",
          boxSizing: "border-box",
        }}
      >
        {!isMobile && (
          <p style={{
            margin: "0 0 16px", fontSize: 11, fontWeight: 700,
            color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.08em",
          }}>
            Explore debt collection
          </p>
        )}

        <div style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "repeat(4, 1fr)",
          gap: 10,
        }}>
          {NAV_ITEMS.map(({ label, desc, route, iconBg, icon }) => (
            <button key={route} className="nd-item" onClick={() => handleNav(route)}>
              <div style={{
                width: 36, height: 36, borderRadius: 9,
                background: iconBg, border: "1px solid #E2E8F0",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}>
                {icon}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <span className="nd-title" style={{
                  fontSize: 14, fontWeight: 700, color: "#0F172A",
                  lineHeight: 1.3, transition: "color 0.15s",
                }}>
                  {label}
                </span>
                <span style={{ fontSize: 12, color: "#64748B", lineHeight: 1.4 }}>
                  {desc}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>
    </>
  );
};

export default NavigationDropdown;